import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Pipe, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatToolbarModule } from '@angular/material/toolbar';
import { SimplebarAngularModule } from 'simplebar-angular';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { debounceTime, firstValueFrom, Subject, window } from 'rxjs';
import { signal } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { NavigationRequest, RouteResponse, SearchResult, Stop } from '@app/services/model/search';
import { SearchService } from '@app/services/search.service';
import { Station } from '@app/services/model/search';
import { SearchControlService } from '../map/controls/services/search-control.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { formatDistance, formatDuration, prettyAddress } from '@app/shared/utils';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { getUserCoordinates } from '@app/shared/utils/location';
import { StationService } from '@app/services/station.service';
import { AdNativeComponent } from '@app/shared/components/ad-native/ad-native.component';
import { AdBannerComponent } from '@app/shared/components/ad-banner/ad-banner.component';

interface Waypoint {
  name: string;
  latLng?: number[];
  placeId?: string;
  searchResults?: SearchResult[];
  stations?: Station[];
  station?: Station;
  distance?: string;
  duration?: string;
}

@Component({
  selector: 'app-navigation',
  standalone: true,
  imports: [
    CommonModule,
    SimplebarAngularModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    DragDropModule,
    MatAutocompleteModule,
    MatDividerModule,
    RouterLink,
    MatProgressSpinnerModule,
    AdNativeComponent,
    AdBannerComponent,
  ],
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.scss',
  animations: [
    trigger('slideAnimation', [
      state('expanded', style({
        left: '0'
      })),
      state('collapsed', style({
        left: 'var(--width)'
      })),
      transition('expanded <=> collapsed', animate('0.2s ease-in-out'))
    ])
  ]
})
export class NavigationComponent implements OnInit, OnDestroy, AfterViewInit {
  private searchSubject = new Subject<Waypoint>();
  private readonly debounceTimeMs = 300;

  @ViewChild('autoFocus') autoFocusField: ElementRef;

  prettyAddress = prettyAddress;

  waypoints = signal<Waypoint[]>([{ name: '' }, { name: '' }]);
  routeData = signal<RouteResponse | null>(null);

  isLoading = signal(false);

  errorMessage = '';

  formatDuration = formatDuration;
  estimatedTime = 1_500;
  totalEstimatedTime = 0;
  totalEstimatedTimeIncludesDuration = 0;

  constructor(private stationService: StationService, private searchService: SearchService, public searchControlService: SearchControlService, private router: Router, private route: ActivatedRoute) {}

  ngOnInit() {
    this.searchSubject
      .pipe(debounceTime(this.debounceTimeMs))
      .subscribe((searchValue) => {
        this.performSearch(searchValue);
      });

    // Auto fill user location
    (async () => {
      try {
        const userLocation = await getUserCoordinates();
        if (userLocation) {
          this.waypoints.update(waypoints => {
            waypoints[0] = {
              name: 'Your location',
              latLng: [userLocation.latitude, userLocation.longitude],
            };
            return waypoints;
          });
        }
      } catch {
        //
      }
    })();

    // Auto fill the destination location, if it's provided in the query params
    (async () => {
      const destination = this.searchControlService.autoFillDestination();
      if (destination) {
        this.isLoading.set(true);

        let data = null;
        if (destination.startsWith('place_id/')) {
          const [_, placeId] = destination.split('/');
          const geocode = await firstValueFrom(this.searchService.geocode(placeId, 'place'));

          if (geocode) {
            data = {
              address: geocode.address,
              placeId: placeId,
              name: geocode.address.formattedAddress,
              isStation: false,
            };
          }
        } else {
          const origin = this.searchControlService.getMapCenterCoords();
          const results = await firstValueFrom(this.searchService.autocomplete(
            destination,
            origin,
          ))
          data = results[0];
        }

        if (data) {
          await this.addressSelected(1, data);
          // remove the last empty waypoint
          if (this.waypoints().at(-1)?.name === '') {
            this.waypoints.update(waypoints => waypoints.slice(0, -1));
          }
          this.errorMessage = '';
        } else {
          this.errorMessage = 'Location not found';
        }

        this.isLoading.set(false);
      }
    })();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.autoFocusField.nativeElement.focus();
    }, 100)
  }

  ngOnDestroy(): void {
    this.waypoints.set([{ name: '' }, { name: '' }]);
    this.routeData.set(null);

    this.searchControlService.clearNavigationPlanner();
    this.searchControlService.enableStationPins();
    this.searchControlService.autoFillDestination.set(null);
    this.searchControlService.isNavigationCollapsed = false;

    this.searchControlService.navigationPlannerLayers = [];
    this.searchControlService.stationPreviewLayers = [];
    this.searchControlService.stationPreviewIds = [];
  }

  addWaypoint() {
    this.waypoints.set([
      ...this.waypoints().slice(0, -1),
      { name: '' },
      this.waypoints().at(-1)!, // Get the last waypoint
    ]);
  }

  updateWaypoint(index: number, event) {
    const updatedWaypoints = [...this.waypoints()];
    updatedWaypoints[index].name = event.target.value;

    this.waypoints.set(updatedWaypoints);
    this.searchSubject.next(updatedWaypoints[index]);
  }

  switchRoute() {
    const origin = this.waypoints().at(0);
    const destination = this.waypoints().at(-1);

    this.waypoints.update(waypoints => {
      waypoints[0] = destination;
      waypoints[waypoints.length - 1] = origin;

      return waypoints;
    });

    this.performNavigation(false);
  }

  removeWaypoint(index: number) {
    const updatedWaypoints = this.waypoints().filter((_, i) => i !== index);
    this.waypoints.set(updatedWaypoints);

    this.performNavigation(false);
  }

  removeWaypointStation(index: number, stationId: number) {
    const updatedWaypoints = this.waypoints().map((waypoint) => {
      waypoint.stations = waypoint.stations.filter(station => station.id !== stationId);
      return waypoint;
    });
    this.waypoints.set(updatedWaypoints);

    this.performNavigation(false);
  }

  drop(event: CdkDragDrop<Waypoint[]>) {
    const updatedWaypoints = [...this.waypoints()];
    moveItemInArray(updatedWaypoints, event.previousIndex, event.currentIndex);
    this.waypoints.set(updatedWaypoints);

    this.performNavigation(false);
  }

  private performSearch(waypoint: Waypoint) {
    if (!waypoint.name) {
      const updatedWaypoints = [...this.waypoints()];
      updatedWaypoints[this.waypoints().indexOf(waypoint)].searchResults = [];
      this.waypoints.set(updatedWaypoints);
      return;
    }

    const origin = this.searchControlService.getMapCenterCoords();
    const search$ = this.searchService.autocomplete(
      waypoint.name,
      origin,
    );

    search$.subscribe((results) => {
      const updatedWaypoints = [...this.waypoints()];
      updatedWaypoints[this.waypoints().indexOf(waypoint)].searchResults = results;

      this.waypoints.set(updatedWaypoints);
    });
  }

  async addressSelected(waypointIndex: number, data: any) {
    const searchResult = data?.value ? JSON.parse(data.value) : data;
    if (searchResult.isStation) {
      const station = (await firstValueFrom(this.stationService.getStationById([searchResult.placeId]))).data.stationById[0];

      this.waypoints.update(waypoints => {
        waypoints[waypointIndex].name = searchResult.address.formattedAddress;
        waypoints[waypointIndex].latLng = [station?.location?.coordinates?.[1], station?.location?.coordinates?.[0]];
        waypoints[waypointIndex].station = station as any;

        return waypoints;
      });
    } else {
      this.waypoints.update(waypoints => {
        waypoints[waypointIndex].name = searchResult.address.formattedAddress;
        waypoints[waypointIndex].placeId = searchResult.placeId;
        waypoints[waypointIndex].station = searchResult.isStation;

        return waypoints;
      });
    }

    // Add new waypoint once the destination is selected
    if (this.waypoints().at(-1).name) {
      this.waypoints.update(waypoints => [...waypoints, { name: '' }])
    }

    this.performNavigation();
  }

  convertJSON(searchResult: SearchResult) {
    return JSON.stringify(searchResult);
  }

  async performNavigation(moveMap = true) {
    const allRoutes = this.waypoints().filter((value) => value.placeId || value.station || value.latLng);
    if (allRoutes.length < 2) {
      return;
    }

    const origin = allRoutes.at(0)!;
    const destination = allRoutes.at(-1)!;
    const waypoints = allRoutes.slice(1, -1);

    const payload: NavigationRequest = {
      origin: this.convertWaypointToPayload(origin),
      destination: this.convertWaypointToPayload(destination),
      intermediates: waypoints.map(this.convertWaypointToPayload),
    }

    this.searchService.route(payload).subscribe((results) => {
      const route = results?.routes?.[0];
      if (route) {
        route.legs.forEach((leg, index) => {
          this.waypoints.update(waypoints => {
            waypoints[index].distance = formatDistance(leg.distanceMeters || 0);
            waypoints[index].duration = formatDuration(Number(leg.duration));
            return waypoints;
          });
        });
        this.totalEstimatedTime = this.estimatedTime * this.waypoints().filter(w => w.station).length;
        this.totalEstimatedTimeIncludesDuration = (this.estimatedTime * this.waypoints().filter(w => w.station).length) + Number(route.duration);

        this.routeData.set({
          ...route,
          distanceMeters: formatDistance(Number(route.distanceMeters) || 0),
          duration: formatDuration(Number(route.duration)),
        });
        this.searchControlService.updateNavigationPlanner(route, moveMap);
        this.searchControlService.showNavigationPins(route, (legIndex, stationIndex) => {
          this.waypoints.update(waypoints => {
            const station = route.legs[legIndex].stations[stationIndex];
            if (waypoints?.find((item) => item.station?.id === station.id)) {
              waypoints = waypoints.filter(s => s.station?.id !== station.id);
            } else {
              const legWaypoint = waypoints.filter((item) => !item.station).find((_, index) => index === legIndex);
              const newIndex = waypoints.findIndex((waypoint) => waypoint?.placeId === legWaypoint?.placeId);

              waypoints.splice(newIndex === waypoints.filter(item => item.name).length - 1 ? newIndex : newIndex + 1, 0, {
                name: station.name,
                station,
              });
            }
            return waypoints;
          })

          // refresh routes after adding stations
          this.performNavigation(false);
        });

        this.errorMessage = '';
      } else {
        this.routeData.set(null);
        this.searchControlService.clearNavigationPlanner();
        this.errorMessage = 'No route found';
      }
    });
  }

  convertWaypointToPayload(waypoint: Waypoint): Stop {
    if (!waypoint.station && waypoint.placeId) {
      return {
        place: {
          id: waypoint.placeId,
        },
      }
    }

    if (waypoint.latLng) {
      return {
        location: {
          latLng: {
            latitude: waypoint.latLng[0],
            longitude: waypoint.latLng[1],
          },
          heading: 0,
        },
      }
    }

    if (waypoint.station && !waypoint.placeId) {
      return {
        location: {
          station: waypoint.station,
          latLng: waypoint.station.location,
          heading: 0,
        },
        address: waypoint.placeId ? undefined : waypoint?.name,
      }
    }

    return {
      place: waypoint?.placeId ? {
        id: waypoint.placeId,
      } : undefined,
      address: waypoint.placeId ? undefined : waypoint?.name,
    }
  }

  goBack() {
    this.router.navigate(['/']);
  }

  togglePanel() {
    this.searchControlService.isNavigationCollapsed = !this.searchControlService.isNavigationCollapsed;
  }

  clearWaypoint(index: number, event: MouseEvent) {
    const waypoint = this.waypoints().at(index);
    if (waypoint.name === 'Your location') {
      this.waypoints.update(waypoints => {
        waypoints[index].name = '';
        waypoints[index].latLng = null;
        return waypoints;
      });
      (event.target as any).value = '';
    }
  }

  getWaypointResults() {
    return this.waypoints().filter(w => w.name);
  }

  getStationName() {
    if (this.waypoints().filter(w => w.station).length === 1) {
      return this.waypoints().filter(w => w.station).at(0).station.name;
    }

    return null;
  }

  isChargerExists() {
    return this.waypoints().filter(w => w.station).length > 0;
  }
}
